<template>
  <!--
    Compact layout: collapses the card into a single inline row so a board
    in compact view can show many players at a glance. Shows the two main
    weapon icons (falling back to the role-color strip square when a slot
    is empty) plus a single nickname — role badge, discord handle, weapon
    slots, builds popup, and builds chip are all hidden.

    The full-height left/right path strips on the <article> are visible at
    the edges (same 4px-wide strips driven by `leftStripClass` /
    `rightStripClass`). The compact inner div uses `bg-transparent` so
    the article's `bg-bg-surface` shows through and the strips remain
    visually prominent at the edges.
  -->
  <!--
    `data-drag-handle` on the root mirrors the pre-refactor behaviour: in
    compact view the whole card is the drag handle. SortableJS reads this
    via `handle: '[data-drag-handle]'` in `useDragAndDrop`. The tag-filter
    buttons inside mark themselves `data-no-drag` so a click on a tag
    toggles the filter rather than starting a drag.
  -->
  <div
    data-drag-handle
    class="relative flex w-full items-center gap-1.5 overflow-hidden bg-transparent rounded-md pl-3 pr-2 py-0.5 select-none"
    :title="compactNickname"
  >
    <!-- First main weapon icon (left side). Falls back to the role-color square when no weapon is picked. -->
    <img
      v-if="weaponAtDisplay(0)"
      :src="weaponAtDisplay(0)?.icon"
      :alt="weaponAtDisplay(0)?.name ?? ''"
      class="w-6 h-6 object-cover rounded-sm shrink-0"
      draggable="false"
    />
    <span
      v-else
      class="inline-block w-6 h-6 rounded-sm shrink-0"
      :class="leftStripClass"
      aria-hidden="true"
    />
    <!-- Second main weapon icon. Falls back to `rightStripClass`, which mirrors the first weapon's color when only one is equipped. -->
    <img
      v-if="weaponAtDisplay(1)"
      :src="weaponAtDisplay(1)?.icon"
      :alt="weaponAtDisplay(1)?.name ?? ''"
      class="w-6 h-6 object-cover rounded-sm shrink-0"
      draggable="false"
    />
    <span
      v-else
      class="inline-block w-6 h-6 rounded-sm shrink-0"
      :class="rightStripClass"
      aria-hidden="true"
    />
    <span class="basis-1/2 min-w-0 flex-1 truncate text-sm font-semibold text-text-primary">{{ compactNickname }}</span>
    <button
      v-if="canManageRsvp"
      data-no-drag
      type="button"
      class="inline-flex min-h-5 shrink-0 items-center gap-1 rounded-full border border-border-strong bg-bg-elevated px-1.5 text-xs font-semibold text-accent transition-[border-color,color,opacity] duration-120 hover:border-accent hover:text-accent-hover focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent disabled:cursor-wait disabled:opacity-60"
      :disabled="rsvpPending"
      :aria-busy="rsvpPending ? 'true' : 'false'"
      :aria-label="rsvpActionLabel"
      :title="signState.label"
      @click="emit('rsvp-toggle')"
    >
      <span
        class="w-1.5 h-1.5 rounded-full shrink-0"
        :class="player.hasSignedIn ? 'bg-signed-point' : 'bg-text-muted'"
        aria-hidden="true"
      />
      {{ rsvpActionText }}
    </button>
    <span
      v-else
      class="w-1.5 h-1.5 rounded-full shrink-0"
      :class="player.hasSignedIn ? 'bg-signed-point' : 'bg-text-muted'"
      :title="signState.label"
    >
      <span class="sr-only">{{ signState.label }}</span>
    </span>
    <span v-if="rsvpError" role="alert" class="sr-only">{{ rsvpError }}</span>
    <div v-if="primaryTag" class="ml-auto flex min-w-0 shrink-0 items-center justify-end gap-1">
      <button
        data-no-drag
        type="button"
        class="inline-flex min-h-5 min-w-0 max-w-24 shrink-0 items-center whitespace-nowrap rounded-full px-1.5 text-xs font-semibold tracking-[0.02em] cursor-pointer transition-opacity duration-120 hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent 2xl:max-w-32"
        :class="tagChipClassFor({ color: primaryTag.color, active: isActiveTagFilter(primaryTag.id) })"
        :title="messages.player.filterByTag(tagDisplayName(primaryTag.name))"
        :aria-label="messages.player.toggleTagFilter(tagDisplayName(primaryTag.name))"
        :aria-pressed="isActiveTagFilter(primaryTag.id) ? 'true' : 'false'"
        @click="onTagToggle(primaryTag.id)"
      >
        <span class="min-w-0 truncate">{{ tagDisplayName(primaryTag.name) }}</span>
      </button>
      <span
        v-if="additionalTags.length > 0"
        data-no-drag
        role="note"
        tabindex="0"
        class="inline-flex min-h-5 shrink-0 items-center rounded-full bg-bg-surface px-1.5 text-xs font-semibold text-text-secondary"
        :title="additionalTagsDescription"
        :aria-label="additionalTagsDescription"
      >+{{ additionalTags.length }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { tagChipClassFor, tagDisplayName } from '@/features/gvg-planner/composables'
import { WEAPONS_BY_SLUG } from '@/data/weapons'
import type { Weapon } from '@/types/weapon'
import type { Player, PlayerTag } from '../../types'
import { boardEn } from './locales/en.ts'
import { boardRu } from './locales/ru.ts'
import { useGvgPlannerStore } from '../../stores/gvgPlannerStore'

export interface PlayerCardCompactProps {
  readonly player: Player
  /** Raid snapshots own a per-raid Discord RSVP instead of the live-board OAuth status. */
  readonly isRaid: boolean
  /** Single-line nickname shown to the right of the weapon icons. */
  readonly compactNickname: string
  /** Tailwind class for the left full-height path strip (mirrors PlayerCard's article-level strip). */
  readonly leftStripClass: string
  /** Tailwind class for the right full-height path strip. */
  readonly rightStripClass: string
  /** Whether this compact card is the signed-in user's raid snapshot row. */
  readonly canManageRsvp: boolean
  /** RSVP acknowledgement is waiting for the subscription snapshot. */
  readonly rsvpPending: boolean
  /** Accessible compact-mode RSVP error. */
  readonly rsvpError: string | null
}

const catalog = defineLocaleCatalog({ en: boardEn, ru: boardRu })
const messages = useLocaleMessages(catalog)
const props = defineProps<PlayerCardCompactProps>()

interface SignState {
  readonly label: string
}

const signState = computed<SignState>(() => {
  if (props.isRaid) {
    return props.player.hasSignedIn
      ? { label: messages.value.player.signedUpAria }
      : { label: messages.value.player.notSignedUpAria }
  }
  return props.player.hasSignedIn
    ? { label: messages.value.player.signedInAria }
    : { label: messages.value.player.notSignedAria }
})

const rsvpActionText = computed<string>(() => {
  if (props.rsvpPending) return messages.value.player.rsvpPending
  return props.player.hasSignedIn
    ? messages.value.player.signOutRaid
    : messages.value.player.signInRaid
})

const rsvpActionLabel = computed<string>(() => props.player.hasSignedIn
  ? messages.value.player.signOutRaidAria
  : messages.value.player.signInRaidAria,
)

const emit = defineEmits<{
  (e: 'rsvp-toggle'): void
}>()

const store = useGvgPlannerStore()

/**
 * Keep the active filter visible so it can always be toggled off from the
 * card. Without an active assigned tag, preserve the backend tag order and
 * show the first tag as the compact card's single actionable chip.
 */
const primaryTag = computed<PlayerTag | undefined>(() => (
  props.player.tags.find((tag) => store.isActiveTagFilter(tag.id))
  ?? props.player.tags[0]
))

/**
 * The remaining assigned tags after the primary chip. Drives the `+N`
 * summary so a player with many tags still surfaces them all to keyboard
 * and screen-reader users without forcing every chip into the bounded
 * button row. The local `primaryId` binding is derived inside the computed
 * so the filter callback has access to a stable, non-ref value while still
 * tracking `primaryTag` reactively.
 */
const additionalTags = computed<ReadonlyArray<PlayerTag>>(() => {
  const primaryId = primaryTag.value?.id
  return props.player.tags.filter((tag) => tag.id !== primaryId)
})

/**
 * Localized tooltip / aria-label for the `+N` summary. Runs the
 * emoji-aware `tagDisplayName` over each hidden tag so a Russian-tagged
 * player gets the full set in cyrillic transliteration rather than the
 * raw slug.
 */
const additionalTagsDescription = computed<string>(() => (
  messages.value.player.additionalTagsDescription(
    additionalTags.value.map((tag) => tagDisplayName(tag.name)).join(', '),
  )
))

/**
 * Build the two displayed main-weapon slots in store-insertion order.
 * Stable-sort keeps the same keying as `PlayerWeaponSlots` so the
 * compact view visually matches the detail view when both modes are
 * rendered side by side.
 */
interface MainSlot {
  readonly slug: string | undefined
  readonly key: string
}

const mainSlots = computed<ReadonlyArray<MainSlot>>(() => {
  const w = props.player.mainWeapons
  if (w.length === 0) {
    return [
      { slug: undefined, key: '-1' },
      { slug: undefined, key: '-2' },
    ]
  }
  const indexed = w
    .slice()
    .map((slug, storeIndex) => ({ slug, storeIndex }))
    .sort((a, b) => a.storeIndex - b.storeIndex)
  const first = indexed[0]
  if (!first) {
    return [
      { slug: undefined, key: '-1' },
      { slug: undefined, key: '-2' },
    ]
  }
  const second = indexed[1]
  if (w.length === 1 || !second) {
    return [
      { slug: first.slug, key: String(first.storeIndex) },
      { slug: undefined, key: '-2' },
    ]
  }
  return [
    { slug: first.slug, key: String(first.storeIndex) },
    { slug: second.slug, key: String(second.storeIndex) },
  ]
})

/**
 * Resolve the Weapon at the given DISPLAY position (0 or 1) inside the
 * card. Used by the compact-mode icons (falling back to the role-color
 * strip square when the slot is empty).
 */
function weaponAtDisplay(displayIdx: 0 | 1): Weapon | undefined {
  const slot = mainSlots.value[displayIdx]
  const slug = slot?.slug
  return slug ? WEAPONS_BY_SLUG.get(slug) : undefined
}

function isActiveTagFilter(tagId: string): boolean {
  return store.isActiveTagFilter(tagId)
}

function onTagToggle(tagId: string): void {
  if (store.isActiveTagFilter(tagId)) {
    store.setActiveTagFilter(null)
    return
  }
  store.setActiveTagFilter(tagId)
}
</script>
